import { useState, useRef, FC, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Button, Icon, ProgressBarBox, Typography } from '@/components/common';
import Icons from '@/const/icons';
import useModal from '@/hooks/useModal';
import { InputText } from '@/components/form/input-text';
import { Textarea } from '@/components/form';
import clsx from 'clsx';
import { PreviewInstructionsModal } from './PreviewInstructionsModal';
import { formatErrorMessage } from '@/lib';
import { useNotification } from '@/hooks';
import { DocumentRequiredPayloadItem } from '@/interfaces';
import { caseApplicantService } from '@/api/caseApplicant';
import { useQueryClient } from '@tanstack/react-query';
import { awsService, GET_DOCUMENT_REQUIRED } from '@/api';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import Images from '@/const/images';
import Image from 'next/image';

interface AddInstructionsDocProps {
	documentsData: DocumentRequiredPayloadItem;
}

export const AddInstructionsDoc: FC<AddInstructionsDocProps> = ({
	documentsData,
}) => {
	const { Modal, show, hide } = useModal();
	const { register, handleSubmit, watch, setValue, reset } = useForm();
	const [file, setFile] = useState<File | null>(null);
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const refInputFile = useRef<HTMLInputElement>(null);
	const queryClient = useQueryClient();

	const { toastError, toastSuccess } = useNotification();

	const instructionTitle = watch('instructionTitle');
	const instructions = watch('instructions');

	const isPreviewDisabled =
		!instructionTitle?.trim() ||
		!instructions?.trim() ||
		!imageUrl ||
		isUploading;

	useEffect(() => {
		fillInstructionsData();
	}, [documentsData]);

	const fillInstructionsData = () => {
		if (documentsData.instructions) {
			setValue('instructionTitle', documentsData.instructions?.title ?? '');
			setValue('instructions', documentsData.instructions?.description ?? '');
			const existingUrl = documentsData.instructions?.image_url;
			if (existingUrl) {
				setImageUrl(existingUrl);
				setProgress(100);
			}
		} else {
			reset();
		}
	};

	const onSubmit = async (data: FieldValues) => {
		if (!imageUrl) return;

		try {
			const documetPayload = {
				name: documentsData.name,
				skip_document: documentsData.skip_document,
				criteria: documentsData.criteria,
				instructions: {
					title: data.instructionTitle,
					description: data.instructions,
					image_url: imageUrl,
				},
			};

			await caseApplicantService.updateCurrentDocument(
				documentsData.id ?? 0,
				documetPayload as any
			);

			queryClient.refetchQueries({ queryKey: [GET_DOCUMENT_REQUIRED] });
			toastSuccess('Instructions saved successfully');
			hide();
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		}
	};

	const uploadAndSetImage = async (newFile: File) => {
		setFile(newFile);
		setImageUrl(null);
		setIsUploading(true);
		setProgress(0);
		try {
			const url = (await awsService.uploadImage(newFile)).data.data.location;
			setImageUrl(url);
			setProgress(100);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
			setFile(null);
		} finally {
			setIsUploading(false);
		}
	};

	const handleDropFile = (e: React.DragEvent) => {
		e.preventDefault();
		const newFile = e.dataTransfer.files[0];
		if (!newFile) return;
		if (newFile.type.includes('image')) {
			uploadAndSetImage(newFile);
		} else {
			toastError('File must be an image');
		}
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newFile = e.target.files?.[0];
		if (!newFile) return;
		if (newFile.type.includes('image')) {
			uploadAndSetImage(newFile);
		} else {
			toastError('File must be an image');
		}
	};

	const onClickUpload = () => {
		refInputFile?.current?.click();
	};

	const handleRemoveFile = () => {
		setFile(null);
		setImageUrl(null);
		setProgress(0);
		if (refInputFile.current) {
			refInputFile.current.value = '';
		}
	};

	const handlePreviewOpen = () => {
		hide();
		setIsPreviewOpen(true);
	};

	const handlePreviewClose = () => {
		setIsPreviewOpen(false);
		show();
	};

	return (
		<>
			<button
				onClick={() => {
					fillInstructionsData();
					show();
				}}
				onDoubleClick={(e) => e.stopPropagation()}
				type="button"
				className="border-[#FD98344D] font-semibold text-main-orange bg-[#FD983426] border rounded-full text-sm px-3 py-2 flex items-center gap-2 ml-auto whitespace-nowrap"
			>
				<Icon
					src={Icons.editSquare}
					className="text-main-orange"
					width={16}
					height={16}
				/>
				{documentsData.instructions ? 'Edit' : 'Add'} Instructions
			</button>

			<Modal
				title={
					<div className="flex flex-col gap-1">
						<span>
							{documentsData.instructions ? 'Edit' : 'Add'} Instructions
						</span>
						<Typography type="regular-14" className="text-opacity-black">
							Document: <span className="text-black">Receipt</span>
						</Typography>
					</div>
				}
				backgroundColor="linear"
				classNameCardContainer="w-full md:!w-[700px] !px-0 !py-0 !pt-8"
				classNameContainerChildren="!px-0 !py-0 max-h-[85vh] 2xl:max-h-[80vh] flex flex-col"
				classNameTitle="pl-8 text-left !mb-4"
				withXButton
				noMaxHeight
			>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col h-full overflow-hidden rounded-b-[32px] border-t border-grey-100"
				>
					<div className="flex flex-col gap-6 px-8 py-4 overflow-y-auto flex-1 scroll-custom">
						<div className="p-3 bg-[#2084E71A] rounded-lg flex items-center gap-2">
							<InformationCircleIcon className="text-blue-link shrink-0 w-4 h-4" />
							<Typography
								type="regular-12"
								className="text-blue-link text-left"
							>
								Clear instructions help clients understand exactly what to
								upload. Include what the document should show, and any specific
								requirements.
							</Typography>
						</div>

						<InputText
							title="Instruction Title"
							id="instructionTitle"
							name="instructionTitle"
							register={register}
							customPlaceholder="Upload a clear photo of your purchase receipt."
							maxLength={70}
							showCount
						/>

						<Textarea
							title="Instructions"
							id="instructions"
							name="instructions"
							register={register}
							customPlaceholder="Please upload a clear image or scan of your purchase receipt. Ensure the receipt shows the store name, date of purchase, item details, and total amount. Accepted file formats are JPG, and PNG. Each file must not exceed 5MB in size."
							rows={4}
							resizable={false}
						/>

						<div className="flex flex-col gap-2">
							<div className="flex flex-col">
								<Typography
									type="regular-16"
									className="text-black mb-1 text-left"
								>
									Reference Image
								</Typography>
								<Typography
									type="regular-12"
									className="text-opacity-black text-left"
								>
									Upload a sample image to visually guide the client. This will
									be displayed alongside the instructions.
								</Typography>
							</div>

							<div
								className={clsx(
									'mt-2 border-2 rounded-2xl border-dashed border-[#E5E7EB] bg-[#F9FAFB] flex flex-col items-center justify-center p-8 transition-colors',
									'border-gray-300 hover:border-main-purple'
								)}
								onDrop={handleDropFile}
								onDragOver={(e) => e.preventDefault()}
							>
								{!file && !imageUrl ? (
									<div
										className="flex flex-col items-center gap-3 cursor-pointer w-full"
										onClick={onClickUpload}
									>
										<span className="bg-[#8A5AFB1A] p-4 rounded-full">
											<Image
												src={Images.uploadCloud}
												alt="upload"
												width={32}
												height={32}
											/>
										</span>
										<UploadLabel />
										<Button
											decoration="primary"
											label="Browse Image"
											icon={Icons.gallery}
											iconRight
											size="fit"
											className="!h-[30px] !w-[190px]"
											classNameButton="!size-4"
										/>
									</div>
								) : (
									<div className="flex flex-col gap-2 w-full">
										<UploadLabel />
										<div className="w-full max-w-fit mx-auto">
											<ProgressBarBox
												percent={progress}
												handleRemove={handleRemoveFile}
												fileName={
													file?.name ?? imageUrl?.split('/').pop() ?? 'image'
												}
												isShowPrevFile={false}
											/>
										</div>
									</div>
								)}

								<input
									ref={refInputFile}
									type="file"
									accept="image/png, image/jpeg, image/webp"
									className="hidden"
									onChange={handleImageChange}
								/>
							</div>
						</div>
					</div>

					<div className="flex flex-col sm:flex-row w-full justify-between gap-3 px-8 py-6 border-t border-grey-100 mt-4 bg-white rounded-b-[32px]">
						<Button
							type="button"
							label="Cancel"
							size="fit"
							decoration="secondary"
							onClick={hide}
						/>

						<div className="flex flex-col sm:flex-row gap-3">
							<Button
								type="button"
								label="Preview"
								size="fit"
								decoration="line-primary"
								disabled={isPreviewDisabled}
								onClick={handlePreviewOpen}
							/>
							<Button
								type="submit"
								label="Save Instructions"
								size="fit"
								decoration="primary"
								disabled={isPreviewDisabled}
							/>
						</div>
					</div>
				</form>
			</Modal>

			<PreviewInstructionsModal
				isOpen={isPreviewOpen}
				onClose={handlePreviewClose}
				onBack={handlePreviewClose}
				title={instructionTitle}
				instructions={instructions}
				referenceImageUrl={imageUrl}
			/>
		</>
	);
};

const UploadLabel = () => {
	return (
		<>
			<Typography type="regular-14" className="text-center text-black">
				Click to upload or drag & drop
			</Typography>
			<Typography type="regular-12" className="text-grey-30">
				PNG, JPG, WEBP up to 5MB.
			</Typography>
		</>
	);
};
